import {
	isProficiencyTag,
	isQuestionTypeTag,
	isSkillTag,
} from "@memorang/helpers/tag";
import type { MixedItemTag } from "@memorang/types";
import { getSkillIcon } from "@memorang/ui/constants";
import type { TFunction } from "i18next";
import {
	buildRecommendedSkillTaskBody,
	buildRecommendedSkillTaskTitle,
	buildSkillChips,
} from "../hooks/useRecommendedTasksSection/helpers";
import type {
	RecommendedSkillPick,
	RecommendedSkillTask,
} from "./adapters/dashboard-task-types";
import type { ContextRecommendationResult } from "./backend-context";
import type { RecommendationResult } from "./types";

export const resolveRecommendationCollectionIds = ({
	activeStudyPackCollectionId,
	studyPackCollectionId,
	freeStudyPackCollectionId,
	examCollectionIds = [],
}: {
	activeStudyPackCollectionId?: number;
	studyPackCollectionId?: number;
	freeStudyPackCollectionId?: number;
	examCollectionIds?: number[];
}): number[] =>
	[
		...new Set(
			[
				activeStudyPackCollectionId,
				studyPackCollectionId,
				freeStudyPackCollectionId,
				...examCollectionIds,
			].filter(
				(collectionId): collectionId is number =>
					Number.isInteger(collectionId) && (collectionId ?? 0) > 0,
			),
		),
	].sort((left, right) => left - right);

const byId = (tags: MixedItemTag[]): Map<number, MixedItemTag> =>
	new Map(tags.map((tag) => [tag.id, tag]));

export const buildRecommendedStudyTasks = ({
	result,
	skills,
	proficiencies,
	questionTypes,
	translate,
	onSelectTags,
	onStart,
}: {
	result: RecommendationResult;
	skills: MixedItemTag[];
	proficiencies: MixedItemTag[];
	questionTypes: MixedItemTag[];
	translate: TFunction;
	onSelectTags?: (tagIds: string[]) => void;
	onStart?: (task: RecommendedSkillTask) => void;
}): RecommendedSkillTask[] => {
	if (result.status !== "READY") {
		return [];
	}

	const skillsById = byId(skills);
	const proficienciesById = byId(proficiencies);
	const questionTypesById = byId(questionTypes);

	return result.sessions.flatMap((session) => {
		const skill = skillsById.get(session.tagIds.skill);
		const proficiency = proficienciesById.get(session.tagIds.proficiency);
		const questionType = questionTypesById.get(session.tagIds.questionType);
		if (!skill || !proficiency || !questionType) {
			return [];
		}

		const recommendation: RecommendedSkillPick = {
			skill,
			proficiency,
			questionTypes: [questionType],
			kind: session.mode === "MAINTENANCE" ? "strongSkill" : "weakSkill",
		};
		const tagIds = [
			String(skill.id),
			String(proficiency.id),
			String(questionType.id),
		];
		const task: RecommendedSkillTask = {
			title: buildRecommendedSkillTaskTitle(recommendation, translate),
			status: "NOT_STARTED",
			body: buildRecommendedSkillTaskBody(recommendation, translate),
			chips: buildSkillChips(session.quickStudyMeta, translate),
			kind: "session",
			icon: getSkillIcon(skill.title),
			tagIds,
			recommendation,
			quickStudyMeta: session.quickStudyMeta,
			onPress: () => {
				if (onStart) {
					onStart(task);
					return;
				}
				onSelectTags?.(tagIds);
			},
		};
		return [task];
	});
};

export const buildRecommendedContextStudyTasks = ({
	result,
	tags,
	translate,
	onSelectTags,
	onStart,
}: {
	result: ContextRecommendationResult;
	tags: MixedItemTag[];
	translate: TFunction;
	onSelectTags?: (tagIds: string[]) => void;
	onStart?: (task: RecommendedSkillTask) => void;
}): RecommendedSkillTask[] => {
	if (result.status !== "READY") {
		return [];
	}
	const tagsById = byId(tags);
	return result.sessions.flatMap((session) => {
		const selectedTags = session.quickStudyMeta.tags
			.flat()
			.map((tagId) => tagsById.get(tagId))
			.filter((tag): tag is MixedItemTag => Boolean(tag));
		const skill = selectedTags.find((tag) => isSkillTag(tag));
		const proficiency = selectedTags.find((tag) => isProficiencyTag(tag));
		const questionTypes = selectedTags.filter((tag) => isQuestionTypeTag(tag));
		if (!skill || !proficiency || questionTypes.length === 0) {
			return [];
		}

		const tagIds = selectedTags.map((tag) => String(tag.id));

		const recommendation: RecommendedSkillPick = {
			skill,
			proficiency,
			questionTypes,
			kind: session.mode === "MAINTENANCE" ? "strongSkill" : "weakSkill",
		};

		const task: RecommendedSkillTask = {
			title: buildRecommendedSkillTaskTitle(recommendation, translate),
			status: "NOT_STARTED",
			body: buildRecommendedSkillTaskBody(recommendation, translate),
			chips: buildSkillChips(session.quickStudyMeta, translate),
			kind: "session",
			icon: getSkillIcon(skill.title),
			tagIds,
			recommendation,
			quickStudyMeta: session.quickStudyMeta,
			onPress: () => {
				if (onStart) {
					onStart(task);
					return;
				}
				onSelectTags?.(tagIds);
			},
		};
		return [task];
	});
};
